// Pure helpers for the Reports hub: hierarchy auto-tagging from the agent's
// assignment, title auto-composition, and "is this mine" filtering.
import type { ResultType } from '../../../constants/global';
import type { ICreateSituationReport } from '../../../interface/child-agent';
import type { IObserverPosting } from '../../../interface/observer';
import type { IElectionOfficialByQuery } from '../../../interface/state/IElectionState';
import type {
	ICreateIncidentRequest,
	IIncident,
	IncidentCategory,
	IncidentSeverity,
	IVesrAttachment,
} from '../../../interface/vesr';
import { MAX_INCIDENT_TITLE } from '../../../interface/vesr';
import { createSituationReport } from '../../../lib/situation-report-api';
import { type ISitrepLocationContext, levelLabel } from '../view-submited-result/utils';

export type HubSegment = 'new' | 'mine' | 'nearby';

/** Everything the submit flow needs to auto-tag a report's location. */
export interface IReportContext {
	electionId?: number;
	electionName?: string;
	level?: string;
	stateCode?: string;
	stateName?: string;
	lgaCode?: string;
	lgaName?: string;
	wardCode?: string;
	wardName?: string;
	pollingUnitCode?: string;
	pollingUnitName?: string;
	/** The agent's own assignment entity name — shown in the context chip. */
	ownName?: string;
}

/** Data contract for opening the `reports-hub` right-section panel. */
export interface IReportsHubData {
	initialSegment?: HubSegment;
	/** Full ancestry context (View-Result / coverage drawer entry points). */
	sitrepLocation?: ISitrepLocationContext;
}

const seg = (code: string, n: number): string | undefined => {
	const parts = code.split('-');
	if (parts.length < n) return undefined;
	return parts.slice(0, n).join('-');
};

/**
 * Derives the hierarchy codes from the agent's assignment by dash-segment
 * truncation (codes are dash-segmented, e.g. PU `04-01-01-005` → state `04`,
 * LGA `04-01`, ward `04-01-01`). Only the own-level NAME is known — parent
 * names aren't in the assignment payload; the server resolves the rest (and
 * GPS auto-correlation enriches the location further).
 */
export const contextFromAssignment = (
	official?: IElectionOfficialByQuery | null,
	/**
	 * The agent's own state name, from their profile. The assignment payload has
	 * no parent names, so without this the State row renders a bare ordinal
	 * ("13") and the incident is stored with `stateName: null`.
	 */
	fallbackStateName?: string | null
): IReportContext => {
	if (!official?.assignment?.code) return {};
	const code = official.assignment.code;
	const resultType = (official.assignment.resultType || '').toUpperCase() as ResultType;
	const name = official.assignment.name;

	const ctx: IReportContext = {
		electionId: official.electionId,
		electionName: official.election,
		level: levelLabel(resultType),
		ownName: name,
		stateCode: seg(code, 1),
		stateName: fallbackStateName || undefined,
	};

	switch (resultType) {
		case 'EC8A':
			ctx.lgaCode = seg(code, 2);
			ctx.wardCode = seg(code, 3);
			ctx.pollingUnitCode = code;
			ctx.pollingUnitName = name;
			break;
		case 'EC8B':
			ctx.lgaCode = seg(code, 2);
			ctx.wardCode = code;
			ctx.wardName = name;
			break;
		case 'EC8C':
			ctx.lgaCode = code;
			ctx.lgaName = name;
			break;
		case 'EC8D':
			ctx.stateCode = code;
			ctx.stateName = name;
			break;
		default:
			break;
	}
	return ctx;
};

/**
 * An independent observer's posting as report context. The posting already carries the place
 * names (api.core resolves them), so unlike an agent's assignment the chip never shows bare codes.
 * Codes are cut from the posting's entity code exactly as `contextFromAssignment` does.
 */
export const contextFromObserverPosting = (posting?: IObserverPosting | null): IReportContext => {
	if (!posting?.entityCode || !(posting.electionId > 0)) return {};
	const code = posting.entityCode;
	const resultType = (posting.resultType || '').toUpperCase() as ResultType;
	const depth = code.split('-').length;

	const ctx: IReportContext = {
		electionId: posting.electionId,
		electionName: posting.electionName || undefined,
		level: levelLabel(resultType),
		stateCode: seg(code, 1),
		stateName: posting.stateName || undefined,
	};
	if (depth >= 2) {
		ctx.lgaCode = seg(code, 2);
		ctx.lgaName = posting.lgaName || undefined;
	}
	if (depth >= 3) {
		ctx.wardCode = seg(code, 3);
		ctx.wardName = posting.wardName || undefined;
	}
	if (depth >= 4) {
		ctx.pollingUnitCode = code;
		ctx.pollingUnitName = posting.pollingUnitName || undefined;
	}
	ctx.ownName = ctx.pollingUnitName || ctx.wardName || ctx.lgaName || ctx.stateName;
	return ctx;
};

/** Maps the View-Result entry point's full-ancestry context onto IReportContext. */
export const contextFromSitrepLocation = (loc: ISitrepLocationContext): IReportContext => ({
	electionId: loc.electionId || undefined,
	electionName: loc.electionName || undefined,
	level: loc.level,
	stateCode: loc.state?.code,
	stateName: loc.state?.name,
	lgaCode: loc.localGovernment?.code,
	lgaName: loc.localGovernment?.name,
	wardCode: loc.ward?.code,
	wardName: loc.ward?.name,
	pollingUnitCode: loc.pollingUnit?.code,
	pollingUnitName: loc.pollingUnit?.name,
	ownName: loc.pollingUnit?.name || loc.ward?.name || loc.localGovernment?.name || loc.state?.name,
});

/**
 * Builds the VESR create-incident body. Pure and separate from the wizard so the
 * wire contract can be tested without rendering three steps of UI.
 *
 * The one rule that must never regress: `electionId` is the CORE election id in
 * STRING form. api.vesr matches it with an exact `Eq`, so a report sent without
 * it is stored unscoped — invisible to every election-scoped situation-room
 * query, and silent on the `election_{id}` SignalR room.
 */
export const buildCreateIncidentRequest = (
	ctx: IReportContext,
	input: {
		title: string;
		note?: string;
		category: IncidentCategory;
		severity: IncidentSeverity;
		latitude: number;
		longitude: number;
	}
): ICreateIncidentRequest => ({
	title: input.title.trim().slice(0, MAX_INCIDENT_TITLE),
	description: input.note?.trim() || undefined,
	category: input.category,
	severity: input.severity,
	// Location codes pass through untouched: `seg()` above already yields the
	// canonical core dash-code segments that incidents are stored and filtered by.
	stateCode: ctx.stateCode,
	stateName: ctx.stateName,
	lgaCode: ctx.lgaCode,
	lgaName: ctx.lgaName,
	wardCode: ctx.wardCode,
	pollingUnitCode: ctx.pollingUnitCode,
	latitude: input.latitude,
	longitude: input.longitude,
	electionId: ctx.electionId != null ? String(ctx.electionId) : undefined,
});

/** Rows for the read-only "posting context" chip on the confirm step. */
export const contextRows = (ctx: IReportContext): { label: string; value: string }[] =>
	[
		{ label: 'State', value: ctx.stateName || ctx.stateCode },
		{ label: 'LGA', value: ctx.lgaName || ctx.lgaCode },
		{ label: 'Ward', value: ctx.wardName || ctx.wardCode },
		{ label: 'Polling Unit', value: ctx.pollingUnitName || ctx.pollingUnitCode },
	].filter((r): r is { label: string; value: string } => !!r.value);

const TITLE_NOTE_LEN = 48;

/**
 * Auto-composed title — the agent never authors one from scratch. Editable on
 * the confirm step.
 */
export const composeTitle = (category: IncidentCategory, severity: IncidentSeverity, note: string): string => {
	const base = `${category} · ${severity}`;
	const trimmed = note.trim().replace(/\s+/g, ' ');
	if (!trimmed) return base;
	const head = trimmed.length > TITLE_NOTE_LEN ? `${trimmed.slice(0, TITLE_NOTE_LEN).trimEnd()}…` : trimmed;
	return `${base} — ${head}`;
};

/**
 * "My report" check. Primary: the deterministic reportedBy GUID cached from
 * the agent's own create responses. Fallback: name match against the JWT's
 * FullName (best-effort — names aren't unique).
 */
export const isMine = (incident: IIncident, rememberedId?: string | null, fullName?: string | null): boolean => {
	if (rememberedId && incident.reportedBy === rememberedId) return true;
	if (fullName && incident.reportedByName?.trim() === fullName.trim()) return true;
	return false;
};

/** Video attachments render a play-overlay tile + a <video> lightbox stage. */
export const isVideoAttachment = (attachment: IVesrAttachment): boolean => !!attachment.contentType?.startsWith('video/');

/**
 * Best-effort dual-write into the citizens-agent sitrep pipeline so the admin
 * coverage views keep receiving agent reports. VESR is the source of truth —
 * a failure here is logged and never surfaced or retried.
 */
export const dualWriteSitrep = async (
	ctx: IReportContext,
	title: string,
	description: string,
	coords: { latitude: number; longitude: number },
	mediaUrls: string[]
): Promise<void> => {
	const payload: ICreateSituationReport = {
		situation: description.trim() ? `${title}\n\n${description.trim()}` : title,
		mediaUrl: mediaUrls,
		latitude: String(coords.latitude),
		longitude: String(coords.longitude),
		electionId: String(ctx.electionId || ''),
		election: ctx.electionName || '',
		state: ctx.stateName,
		stateCode: ctx.stateCode,
		lga: ctx.lgaName,
		lgaCode: ctx.lgaCode,
		ward: ctx.wardName,
		wardCode: ctx.wardCode,
		poolingUnit: ctx.pollingUnitName,
		poolingUnitCode: ctx.pollingUnitCode,
	};
	try {
		const out = await createSituationReport(payload);
		if (!out.reachable || out.httpStatus < 200 || out.httpStatus >= 300) {
			console.warn('Citizens-agent sitrep dual-write failed (non-blocking).');
		}
	} catch {
		console.warn('Citizens-agent sitrep dual-write failed (non-blocking).');
	}
};

/** Distance label fallback when the API's distanceDisplay is missing. */
export const distanceLabel = (km?: number, display?: string): string => {
	if (display) return display;
	if (km == null || Number.isNaN(km)) return '';
	return km < 1 ? `${Math.round(km * 1000)} m away` : `${km.toFixed(1)} km away`;
};
